import styles from './Kind.module.scss'
import { useRef } from 'react'
import animationStyles from 'components/common/Animation/Animation.module.scss'
import useElementVisible from 'components/common/Animation/useElementVisible'

const Kind: React.FC = () => {
  const animeRef = useRef<HTMLDivElement>(null)
  const isVisible = useElementVisible(animeRef, 0.1)

  return (
    <section
      className={`${styles.kind} ${animationStyles.fade} ${isVisible ? animationStyles.fadeOn : ''}`}
      ref={animeRef}
    >
      <h2 className={styles.sectionTitle}>プロトタイプの種類</h2>
      <div className={styles.wrapper}>
        <section className={`${styles.kindItem} ${styles.figma}`}>
          <figure className={styles.kindItem__image}>
            <img src="/img/services/prototyping_kind01.webp" alt="Figmaプロトタイプ" width={540} height={304} />
          </figure>
          <h3 className={styles.kindItem__title}>Figmaプロトタイプ</h3>
          <p className={styles.kindItem__text}>
            Figmaプロトタイプを用い、画面遷移やインタラクションを再現した実際の使用感に近いモックアップを制作いたします。デザインの方向性やユーザーフローの検証に最適です。
          </p>
          <dl className={`${styles.kindItem__box} ${styles.featureBox}`}>
            <dt className={styles.kindItem__box__title}>特徴</dt>
            <dd className={styles.kindItem__box__text}>
              画面遷移やインタラクション、デザインの視覚的イメージを短期間で確認でき、変更にも柔軟に対応できるプロトタイピングを実現します。
            </dd>
          </dl>
          <dl className={`${styles.kindItem__box} ${styles.periodBox}`}>
            <dt className={styles.kindItem__box__title}>標準制作期間：1〜2週間</dt>
            <dd className={styles.kindItem__box__text}>
              要件定義・設計：3〜5営業日
              <br />
              UI設計・制作：5〜7営業日
              <br />
            </dd>
          </dl>
        </section>
        <section className={`${styles.kindItem} ${styles.technical}`}>
          <figure className={styles.kindItem__image}>
            <img src="/img/services/prototyping_kind02.webp" alt="テクニカルプロトタイプ" width={540} height={304} />
          </figure>
          <h3 className={styles.kindItem__title}>テクニカルプロトタイプ</h3>
          <p className={styles.kindItem__text}>
            最新のAIツールを活用し、フロントエンド実装を含む動作する高度なプロトタイプを迅速に制作します。複雑な機能や実際のデータ連携を検証したい場合に適しています。
          </p>
          <dl className={`${styles.kindItem__box} ${styles.featureBox}`}>
            <dt className={styles.kindItem__box__title}>特徴</dt>
            <dd className={styles.kindItem__box__text}>
              実際に動作する機能を実装することで、フロントエンドの挙動検証や複雑なインタラクションの再現が可能になり、技術的な実現可能性を早期に確認できます。
            </dd>
          </dl>
          <dl className={`${styles.kindItem__box} ${styles.periodBox}`}>
            <dt className={styles.kindItem__box__title}>標準制作期間：3〜5週間</dt>
            <dd className={styles.kindItem__box__text}>
              要件定義・アーキテクチャ設計：5〜7営業日
              <br />
              フロントエンド実装：10〜15営業日
              <br />
              テスト・調整：5〜7営業日
            </dd>
          </dl>
          <dl className={`${styles.kindItem__box} ${styles.optionBox}`}>
            <dt className={styles.kindItem__box__title}>オプション（応相談）</dt>
            <dd className={styles.kindItem__box__text}>
              バックエンド連携（Supabaseなどを活用した簡易DB構築）や本番同等の環境へのデプロイ
            </dd>
          </dl>
        </section>
        <section className={`${styles.kindItem} ${styles.user}`}>
          <figure className={styles.kindItem__image}>
            <picture>
              <source srcSet="/img/services/prototyping_kind03.webp" media="(min-width: 1001px)" />
              <source srcSet="/img/services/prototyping_kind03_sp.webp" media="(max-width: 1000px)" />
              <img src="/img/services/prototyping_kind03.webp" alt="ユーザー検証" width={320} height={480} />
            </picture>
          </figure>
          <div className={styles.kindItem__cts}>
            <h3 className={styles.kindItem__title}>ユーザー検証</h3>
            <p className={styles.kindItem__text}>
              プロトタイプを用いたユーザビリティテストを実施します。テストユーザーからのフィードバックを集め、より使いやすいプロダクトへブラッシュアップしていきます。
            </p>
            <dl className={`${styles.kindItem__box} ${styles.featureBox}`}>
              <dt className={styles.kindItem__box__title}>特徴</dt>
              <dd className={styles.kindItem__box__text}>
                ユーザーテストの設計から、リモート・対面での実施、定性・定量データの収集、改善提案レポートの作成までを一貫してサポートします。
              </dd>
            </dl>
            <dl className={`${styles.kindItem__box} ${styles.periodBox}`}>
              <dt className={styles.kindItem__box__title}>標準制作期間：2〜3週間</dt>
              <dd className={styles.kindItem__box__text}>
                テスト計画・設計：3〜5営業日
                <br />
                ユーザーリクルート：5〜7営業日（並行実施）
                <br />
                テスト実施：3〜5営業日
                <br />
                分析・レポート作成：5〜7営業日
              </dd>
            </dl>
            <dl className={`${styles.kindItem__box} ${styles.AIBox}`}>
              <dt className={styles.kindItem__box__title}>AIを活用した検証手法</dt>
              <dd className={styles.kindItem__box__text}>
                AIを活用し、ユーザーの行動パターンから重要なインサイトを自動抽出することで分析時間を短縮。ヒートマップや行動解析により、クリックやスクロール、視線の動きからUIの問題点を視覚的に特定します。さらに、表情や音声から感情反応を測定することで体験の質を数値化し、大量のセッションデータも迅速に処理して一貫したパターンを発見。ユーザーの発言や行動に自動でタグ付け・分類を行い、アクセシビリティなどの課題も浮き彫りにします。
              </dd>
            </dl>
          </div>
        </section>
      </div>
    </section>
  )
}

export default Kind
